import { Alert, Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { ThemeProvider } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import { isFrMobile, toStorage } from '../../utils/frPhone';
import SubmitCessionCertificate from '../DealsList/DealStageActions/SubmitCessionCertificate';
import SubmitCTVO from '../DealsList/DealStageActions/SubmitCTVO';
import legacyTheme from '../../theme';
import { NextThemeProvider, scoreColor, tokens } from '../../theme-next';
import { useAuth } from '../AuthContext';
import { isCgDocType } from '../FormCGIA/ocr/ocrResponseTypes';
import { runCrossChecks } from '../FormCGIA/validation/crossCheckEngine';
import { ALL_CROSS_CHECK_RULES } from '../FormCGIA/validation/crossCheckRules';
import { EntityKindSwitches } from '../FormCGIA/v2/EntityKindSwitches';
import {
  natureAcheteur,
  natureVendeur,
  resoudreNature,
  type NatureChoisie,
} from '../FormCGIA/validation/personneMorale';
import {
  type AuditBatchResult,
  type AuditVerdict,
  auditResultToFindings,
} from '../FormCGIA/v2/auditVerdictAdapter';
import { ctvoV1ParcoursToBannerLabel, ctvoV1ResultToFindings } from '../FormCGIA/v2/ctvoAnalyzeV1Adapter';
import { AlertsColumn } from '../FormCGIA/v2/AlertsColumn';
import { deriveReviewCotitulaire } from '../FormCGIA/v2/acquereurReconcile';
import { CotitulaireSection } from '../FormCGIA/v2/DocumentChecklistColumn';
import { HIDDEN_FROM_AGENCY_RULE_IDS, filterFindingsForDisplay } from '../FormCGIA/v2/findingsDisplay';
import { ParcoursBanner } from '../FormCGIA/v2/ParcoursBanner';
import type { DetectedParcours } from '../FormCGIA/v2/parcoursDetection';
import {
  PARCOURS_SOURCE_DETAILS,
  detectParcours,
  inferParcoursSourceFromLabel,
} from '../FormCGIA/v2/parcoursDetection';
import { StatusPill } from '../FormCGIA/v2/PremiumComponents';
import { AuditBatchVerdict } from './AuditBatchVerdict';
import { AuditStatusBadge, type AuditStatus } from './AuditStatusBadge';
import { libelleFileReview, resolveReviewNavKey } from './reviewNavKeys';
import { effectiveCotitulaireAcheteur, readCotitulaireDecision, showCotitulaireGroup } from './cotitulaireDecision';
import type { CgDocType, CgOcrResponse } from '../FormCGIA/ocr/ocrResponseTypes';
import type { CrossCheckFinding } from '../FormCGIA/validation/crossCheckEngine';
import type { AnalysisSnapshot, UploadedDocResult } from '../FormCGIA/store/useFormCGIAStore';
import type { CtvoAnalyzeV1Result, CtvoStatutGlobal } from '../../types/ctvoAnalyzeV1';
import { stripDealBlobs } from '../../utils/stripDealBlobs';
import { pieceDetachmentEndpoints, requestPieceDetachment } from '../../utils/pieceDetachment';

/**
 * Feature flag local : affichage du verdict d'audit batch sur la page review GOD.
 *
 * Désactivé actuellement — l'audit tourne en sous-marin côté form-cgia
 * (création CG + fulfill complet), le verdict reste persisté en BDD pour
 * usage interne (cron, futur tableau de bord, etc.) mais n'est pas exposé
 * dans cette UI pour ne pas surcharger le god avec un signal redondant.
 *
 * Passer à `true` pour réactiver le composant — tout est encore câblé
 * derrière (endpoint, composant, mutation, query). Pas de dette technique.
 */
const SHOW_AUDIT_BATCH_VERDICT = false;
import {
  ACHETEUR_FIELDS,
  ADRESSE_FIELDS,
  COTITULAIRE_FIELDS,
  FieldDef,
  PAIRED_FIELDS,
  VEHICULE_FIELDS,
  VENDEUR_FIELDS,
  demandeImmatCoversField,
  docTypePriorityRank,
  filterFieldsByEntityKind,
  fmtValue,
  humanizeDocType,
  pieceIdentiteCotitulaireCoversField,
} from './fieldDisplayConfig';
import { FieldEditList, FieldGroup } from './FieldEditList';
import { findFieldLengthViolations } from './fieldLengthLimits';
import {
  extractReviewFieldErrors,
  reviewFieldAnchorId,
  reviewFieldErrorsByRow,
  summarizeReviewFieldErrors,
} from './reviewFieldErrors';
import { fileNameFromUrl } from './docLinksGrouping';
import { InlineFileViewer, ViewerFile } from './InlineFileViewer';
import { GodTribune } from './GodTribune';
import OpenDocumentRequestsPanel from './OpenDocumentRequestsPanel';
import { PiecesGrid } from './PiecesGrid';
import { RejectCGModal } from './RejectCGModal';
import { buildRecepisseDaDoc, type DaReceiptPayload } from './daReceiptDoc';
import { selectPiecesFindings } from './selectPiecesFindings';

// ─────────────────────────────────────────────────────────────
//  Types miroir du payload backend GET /api/cg/:id/review
// ─────────────────────────────────────────────────────────────

interface AggregatedField {
  fieldKey: string;
  value: unknown;
  confidence: number;
  sources: Array<{
    extractionId: number;
    docTypeDetected: string;
    confidence: number;
    value: unknown;
  }>;
}

interface ExtractionRow {
  id: number;
  imageUrl: string;
  docTypeDetected: string;
  docTypeMatch: boolean;
  confidence: number;
  fields: unknown;
  warnings: unknown;
  provider: string | null;
  createdAt: string;
  /**
   * Pièce d'identité que le routage (personne × type × face) n'a rattachée à
   * PERSONNE : elle n'alimente aucun champ d'identité, et c'est voulu — mais
   * le relecteur doit la voir, sinon le dossier semble incomplet alors que le
   * fichier est au dossier, simplement non attribué.
   */
  identityUnrouted?: boolean;
}

interface ExternalCommentRow {
  comment_id: number;
  registration_id: number;
  user_id: string;
  user_role: string;
  user_name: string;
  comment: string;
  created_at: string;
  /** JSON string ('NULL' si absent, cf. COALESCE backend) ou tableau d'URLs. */
  files: string | string[] | null;
}

/**
 * Pièce présente au dossier mais SANS ligne dans `cg_ocr_extractions` : jamais
 * OCRisée, donc jamais vue par les cross-checks ni par l'audit Opus.
 * Deux provenances en pratique : `coBuyerFiles` (pièces du co-titulaire,
 * uploadées sans OCR par design) et `commentFiles` (PJ ajoutées après coup dans
 * un commentaire externe).
 */
interface UnanalyzedDocumentRow {
  url: string;
  sourceType: string;
  label: string;
}

interface CGReviewPayload {
  registration: Record<string, any>;
  extractions: ExtractionRow[];
  aggregatedFields: AggregatedField[];
  validatedFields?: Record<string, { validated_at?: string; validated_by?: number | null; value?: unknown }>;
  /**
   * Pièces du dossier sans extraction OCR. Affichées en fin de carrousel avec
   * un badge « Non analysée » — sans elles, l'inventaire de la review était
   * plus court que celui de list-cg et le relecteur ignorait leur existence
   * (retour François 2026-07-20 EQ-598-TE : 13 fichiers vs 11 affichés).
   */
  unanalyzedDocuments?: UnanalyzedDocumentRow[];
  /** Récépissé de la DA faite via Skipcar sur ce véhicule, s'il en existe un. */
  daReceipt?: DaReceiptPayload | null;
  /** Commentaires externes (agence à la soumission + fil agence↔GOD). */
  externalComments?: ExternalCommentRow[];
  // Photo figée du résultat FormCGIA — source de vérité absolue, rejouée verbatim.
  analysisSnapshot?: AnalysisSnapshot | null;
  // Verdicts LLM bruts — fallback pour les CG legacy sans snapshot.
  analyzeV1?: { statut: CtvoStatutGlobal | 'en_cours' | null; result: CtvoAnalyzeV1Result | null } | null;
  auditBatch?: { verdict: AuditVerdict | null; result: AuditBatchResult | null; modelUsed: string | null } | null;
  // État d'exécution de l'audit IA — alimente le badge « Audit OK / en échec /
  // jamais exécuté » (cf. AuditStatusBadge). Distinct de auditBatch, qui
  // collapse l'échec et l'absence à null.
  auditStatus?: AuditStatus | null;
}

type VoisinReview = { registrationId: number; regPlate: string | null };
type VoisinsReview = {
  queue: string | null;
  position: number | null;
  total: number;
  previous: VoisinReview | null;
  next: VoisinReview | null;
};

interface CGReviewResponse {
  success: boolean;
  data?: CGReviewPayload;
  error?: string;
}

// ─────────────────────────────────────────────────────────────
//  Helpers
//  (les libellés docType, la priorité des chips et la liste des champs par
//  groupe vivent désormais dans ./fieldDisplayConfig — partagés avec l'étape
//  de vérification agence, cf. FormCGIA/v2/buildVerifyGroups.)
// ─────────────────────────────────────────────────────────────

/**
 * Référence cliquable vers une pièce du dossier qui RECOUPE un champ sans en
 * être une source agrégée : le 13750 pour le bloc Acheteur, la CNI du
 * co-titulaire pour le bloc Co-titulaire. Dans les deux cas la pièce range
 * l'info sous d'autres clés OCR que celles du bloc, donc aucune pastille n'y
 * pointait. Purement additif : ces références ne participent jamais à
 * l'arbitrage de la valeur affichée.
 */
interface ReferenceDocRef {
  docType: string;
  docIndex: number;
  rawFields: unknown;
  /** La pièce dit-elle quelque chose sur ce champ ? */
  coversField: (rawFields: unknown, fieldKey: string) => boolean;
}

const buildGroup = (
  title: string,
  accentColor: string,
  defs: FieldDef[],
  agg: Map<string, AggregatedField>,
  extractionIndexById: Map<number, number>,
  // Pastilles de référence du bloc — vide si les pièces concernées sont absentes.
  referenceDocs: ReferenceDocRef[] = [],
): FieldGroup => {
  const fields = defs
    .map((def) => {
      // Première clé candidate qui porte une valeur agrégée — l'ordre du
      // tableau encode la priorité métier (carte grise avant cession).
      const candidateKeys = [def.fieldKey, ...(def.fallbackFieldKeys ?? [])];
      const candidates = candidateKeys.map((k) => agg.get(k)).filter((a): a is AggregatedField => Boolean(a));
      const chosen = candidates[0] ?? null;
      const pairedKey = PAIRED_FIELDS[def.fieldKey];
      const paired = pairedKey ? agg.get(pairedKey) : null;

      // Chips de provenance : union des sources de toutes les clés candidates,
      // dédupliquées par type de document — un recto + verso de la même pièce
      // produit 2 extractions mais doit afficher UNE vignette (on garde
      // l'occurrence la plus confiante pour le jump viewer).
      const bestSourceByDocType = new Map<
        string,
        { docType: string; docTypeLabel: string; docIndex: number; confidence: number }
      >();
      candidates.forEach((a) => {
        a.sources.forEach((s) => {
          const existing = bestSourceByDocType.get(s.docTypeDetected);
          if (!existing || s.confidence > existing.confidence) {
            bestSourceByDocType.set(s.docTypeDetected, {
              docType: s.docTypeDetected,
              docTypeLabel: humanizeDocType(s.docTypeDetected),
              docIndex: extractionIndexById.get(s.extractionId) ?? 0,
              confidence: s.confidence,
            });
          }
        });
      });

      // Règle métier "pièce de référence" (preferredDocType) :
      //  - Si une source de ce docType existe → on n'affiche QUE cette chip,
      //    pour rappeler à l'utilisateur que cette info DOIT venir de là.
      //  - Sinon (CG illisible, etc.) → on retombe sur toutes les chips,
      //    triées par priorité métier, ce qui reste informatif.
      const allSources = Array.from(bestSourceByDocType.values());
      const preferred = def.preferredDocType ? allSources.filter((s) => s.docType === def.preferredDocType) : [];
      const sourcesToShow = preferred.length > 0 ? preferred : allSources;
      sourcesToShow.sort((a, b) => docTypePriorityRank(a.docType) - docTypePriorityRank(b.docType));

      // Pastilles supplémentaires vers les pièces à RECOUPER, qui rangent l'info
      // sous d'autres clés OCR que celles du bloc et n'apparaissent donc jamais
      // dans `sources` :
      //  - bloc Acheteur : le 13750 énonce l'identité de l'acquéreur sous ses
      //    clés `titulaire_*`, exclues de l'agrégation (collision avec le
      //    titulaire carte grise = vendeur, cf. EXCLUDED_FIELD_SOURCES) ;
      //  - bloc Co-titulaire : la CNI du co-titulaire sort ses champs sous les
      //    clés génériques `nom`/`prenom`/…, elles aussi exclues (sans quoi elle
      //    écraserait l'identité du titulaire — CY-517-VW, DM-724-VQ).
      // Ajoutées APRÈS le filtre `preferredDocType` pour rester visibles même
      // quand une pièce de référence est imposée, et dédupliquées si la pièce
      // est déjà une source du champ.
      const referenceChips = referenceDocs
        .filter((ref) => ref.coversField(ref.rawFields, def.fieldKey))
        .map((ref) => ({ docTypeLabel: humanizeDocType(ref.docType), docIndex: ref.docIndex }));

      // Téléphone : normalisé au format FR local (0033…/+33… → 06/07) à l'affichage.
      const rawValue = chosen ? fmtValue(chosen.value) : '';
      const value = def.fieldKey === 'telephone' && rawValue ? toStorage(rawValue) : rawValue;

      return {
        fieldKey: def.fieldKey,
        label: def.label,
        value,
        confidence: chosen ? chosen.confidence : 0,
        pairedFieldKey: pairedKey,
        pairedValue: paired ? fmtValue(paired.value) : '',
        pairedConfidence: paired ? paired.confidence : 0,
        sources: [
          ...sourcesToShow.map(({ docTypeLabel, docIndex }) => ({ docTypeLabel, docIndex })),
          ...referenceChips.filter(
            (c, i) =>
              !sourcesToShow.some((s) => s.docIndex === c.docIndex) &&
              referenceChips.findIndex((o) => o.docIndex === c.docIndex) === i,
          ),
        ],
      };
    });
  // Review (god) : on affiche TOUS les champs attendus du groupe, MÊME VIDES et
  // éditables — le god doit pouvoir compléter une info manquante (pièce absente)
  // avant la soumission MISIV ; un champ requis manquant = CTVO impossible. On ne
  // filtre donc plus les champs vides ici. Les groupes OPTIONNELS entièrement
  // vides (ex. Co-titulaire) sont masqués à l'assemblage (cf. `groups`).
  return { title, accentColor, fields };
};

// ─────────────────────────────────────────────────────────────
//  Page
// ─────────────────────────────────────────────────────────────

const CGReviewInner = () => {
  const { registrationId } = useParams<{ registrationId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const userRole = currentUser?.data?.data?.role;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (userRole && userRole !== 'god') navigate('/list-cg');
  }, [userRole, navigate]);

  const regIdNum = Number(registrationId);
  const validId = Number.isInteger(regIdNum) && regIdNum > 0;

  const { data, isLoading, isError, error } = useQuery<CGReviewResponse>({
    queryKey: ['cgReview', regIdNum],
    queryFn: async () => {
      const response = await makeAuthenticatedApiCall<CGReviewResponse>('get', `/api/cg/${regIdNum}/review`);
      return response.data;
    },
    enabled: validId && userRole === 'god',
  });

  // Voisins dans la file (même cg_status, ordre de la liste god) : l'équipe CG
  // enchaîne les dossiers sans repasser par la liste (sprint du 07/10/2026).
  const { data: voisins } = useQuery<VoisinsReview | null>({
    queryKey: ['cgReviewNeighbors', regIdNum],
    queryFn: async () => {
      const response = await makeAuthenticatedApiCall<{ data: VoisinsReview }>(
        'get',
        `/api/cg/${regIdNum}/review/neighbors`,
      );
      return response.data?.data ?? null;
    },
    enabled: validId && userRole === 'god',
    staleTime: 60_000,
  });
  const voisinsRef = useRef<VoisinsReview | null | undefined>(voisins);
  voisinsRef.current = voisins;

  const ouvrirDossier = useCallback(
    (id: number) => {
      window.scrollTo({ top: 0 });
      navigate(`/cg/${id}/review`);
    },
    [navigate],
  );
  /** Après une action qui sort le dossier de la file : le suivant, sinon la liste. */
  const allerAuSuivantOuListe = useCallback(() => {
    const suivant = voisinsRef.current?.next;
    if (suivant) ouvrirDossier(suivant.registrationId);
    else navigate('/list-cg');
  }, [navigate, ouvrirDossier]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const cible = e.target as HTMLElement | null;
      const action = resolveReviewNavKey({
        key: e.key,
        metaKey: e.metaKey,
        ctrlKey: e.ctrlKey,
        altKey: e.altKey,
        targetTag: cible?.tagName ?? null,
        targetEditable: Boolean(cible?.isContentEditable),
        dialogOpen: document.querySelector('[role="dialog"]') !== null,
      });
      const v = voisinsRef.current;
      if (action === 'next' && v?.next) {
        e.preventDefault();
        ouvrirDossier(v.next.registrationId);
      } else if (action === 'previous' && v?.previous) {
        e.preventDefault();
        ouvrirDossier(v.previous.registrationId);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [ouvrirDossier]);

  // Le bandeau (immat + agence) reste visible au scroll ; le visualiseur, lui
  // aussi sticky, se cale juste en dessous.
  const heroRef = useRef<HTMLDivElement | null>(null);
  const [heroHeight, setHeroHeight] = useState(0);
  useEffect(() => {
    const el = heroRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setHeroHeight(el.getBoundingClientRect().height));
    ro.observe(el);
    return () => ro.disconnect();
  }, [isLoading, isError]);
  const viewerTop = heroHeight > 0 ? Math.round(heroHeight) + 16 : 24;

  // Suppression d'une pièce depuis le visualiseur (SKIP-355) : même API que le
  // retrait depuis la zone de dépôt, puis relecture du dossier.
  const supprimerPiece = useCallback(
    async (file: { url: string }) => {
      await requestPieceDetachment(pieceDetachmentEndpoints.cg(regIdNum), file.url);
      await queryClient.invalidateQueries({ queryKey: ['cgReview', regIdNum] });
      await queryClient.invalidateQueries({ queryKey: ['cgDocumentsStructured', String(regIdNum)] });
    },
    [queryClient, regIdNum],
  );

  const [immatCopiee, setImmatCopiee] = useState(false);
  const copierImmat = useCallback(async (plaque: string) => {
    try {
      await navigator.clipboard.writeText(plaque);
    } catch {
      /* presse-papiers indisponible (http, permissions) : on signale quand même */
    }
    setImmatCopiee(true);
    window.setTimeout(() => setImmatCopiee(false), 1800);
  }, []);

  const [actionLoading, setActionLoading] = useState<'approve' | 'reject' | 'save' | 'ants' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [viewerIndex, setViewerIndex] = useState(0);
  // Décompte des pièces requises, calculé par PiecesGrid qui est le seul à
  // connaître les règles d'exigibilité de CE dossier. `null` tant qu'il n'a pas
  // rendu : on n'affiche pas un compteur inventé en attendant.
  const [pieceCounts, setPieceCounts] = useState<{ present: number; required: number } | null>(null);
  // Référence stable, et écriture seulement sur changement réel de valeur :
  // PiecesGrid notifie depuis un effet qui dépend de ce callback, un handler
  // recréé à chaque rendu y bouclerait indéfiniment.
  // Mêmes clé et route que OpenDocumentRequestsPanel : react-query dédoublonne,
  // il n'y a qu'un appel réseau pour les deux usages.
  const { data: docRequestsPayload } = useQuery<{ data?: Array<{ docType: string; status: string }> }>({
    queryKey: ['cgDocumentRequests', regIdNum],
    queryFn: async () => {
      const response = await makeAuthenticatedApiCall<{ data?: Array<{ docType: string; status: string }> }>(
        'get',
        `/api/cg/${regIdNum}/document-requests`,
      );
      return response.data;
    },
    enabled: Number.isInteger(regIdNum) && regIdNum > 0,
  });
  const openRequestDocTypes = useMemo(
    () => (docRequestsPayload?.data ?? []).filter((r) => r.status === 'open').map((r) => r.docType),
    [docRequestsPayload],
  );

  const handlePieceCountsChange = useCallback((counts: { present: number; required: number }) => {
    setPieceCounts((previous) =>
      previous && previous.present === counts.present && previous.required === counts.required ? previous : counts,
    );
  }, []);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);

  // Ancre du bloc « Document x / y » (InlineFileViewer), situé tout en bas de la
  // page. Les encarts de pièces (« Pièces non analysées »…) se trouvent au-dessus :
  // cliquer « Voir » changeait bien l'index du viewer, mais hors écran — rien ne
  // bougeait visuellement. On ramène donc le viewer dans le champ de vision.
  const documentsSectionRef = useRef<HTMLDivElement | null>(null);

  const showDocument = useCallback((index: number) => {
    setViewerIndex(index);
    documentsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Auto-save des corrections champ par champ : chaque édition est écrite dans les
  // tables réelles du dossier (PATCH /review-fields → flushReviewFields), en debounce,
  // pour que la modif « prenne » sans attendre « Prêt à soumettre » et reste visible
  // au rechargement. Les valeurs en attente sont accumulées puis envoyées en un lot.
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const pendingSaveRef = useRef<Record<string, string>>({});
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Champs refusés par le backend au dernier enregistrement (fieldKey → message),
  // affichés en rouge sous chaque champ + récapitulés dans un bandeau. Les autres
  // champs du même envoi, eux, sont bien enregistrés.
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string>>({});

  // Envoi d'un lot de corrections. En cas d'échec, seuls les champs refusés (ou
  // tout le lot si l'échec n'est pas lié à un champ : réseau, 500) sont remis en
  // attente pour un prochain envoi.
  const sendReviewFields = useCallback(
    async (fields: Record<string, string>): Promise<'saved' | 'fieldErrors' | 'failed'> => {
      const sentKeys = Object.keys(fields);
      const clearSent = (prev: Record<string, string>) =>
        Object.fromEntries(Object.entries(prev).filter(([k]) => !sentKeys.includes(k)));
      setSaveState('saving');
      try {
        await makeAuthenticatedApiCall('patch', `/api/cg/${regIdNum}/review-fields`, { fields });
        setServerFieldErrors(clearSent);
        setSaveState('saved');
        return 'saved';
      } catch (err) {
        const fieldErrors = extractReviewFieldErrors(err);
        const retryKeys = fieldErrors ? fieldErrors.map((e) => e.fieldKey) : sentKeys;
        const retry = Object.fromEntries(Object.entries(fields).filter(([k]) => retryKeys.includes(k)));
        pendingSaveRef.current = { ...retry, ...pendingSaveRef.current };
        if (fieldErrors) {
          setServerFieldErrors((prev) => ({
            ...clearSent(prev),
            ...Object.fromEntries(fieldErrors.map((e) => [e.fieldKey, e.message])),
          }));
        }
        setSaveState('error');
        return fieldErrors ? 'fieldErrors' : 'failed';
      }
    },
    [regIdNum],
  );

  const scheduleAutoSave = useCallback(
    (fieldKey: string, value: string) => {
      pendingSaveRef.current[fieldKey] = value;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        const fields = pendingSaveRef.current;
        pendingSaveRef.current = {};
        if (Object.keys(fields).length === 0) return;
        await sendReviewFields(fields);
      }, 800);
    },
    [sendReviewFields],
  );

  // Flush immédiat des saisies en attente si on quitte la page.
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      const fields = pendingSaveRef.current;
      if (Object.keys(fields).length > 0) {
        makeAuthenticatedApiCall('patch', `/api/cg/${regIdNum}/review-fields`, { fields }).catch(() => {});
      }
    };
  }, [regIdNum]);

  // Avant d'ouvrir une modale de soumission MiSIV (DC / CTVO) depuis /review :
  //  1. on FLUSHE immédiatement les corrections du god encore en attente (sans
  //     attendre le debounce de 800 ms — sinon une modif faite juste avant le
  //     clic partirait à MiSIV avec l'ancienne valeur) ;
  //  2. on RECHARGE le dossier pour que `registration` (passé en `deal` aux
  //     composants de soumission) reflète les valeurs corrigées.
  // Sans ça, la modale — montée une seule fois au chargement de la page —
  // repartait des valeurs d'origine et MiSIV recevait des données non corrigées
  // (bug remonté par François : corrections faites dans /review ignorées à la
  // soumission).
  //
  // Rend `false` (la modale ne s'ouvre pas) si des champs ont été refusés :
  // sinon elle s'ouvrirait sur les anciennes valeurs, sans dire pourquoi — le
  // cas GB-053-TZ du 28/09. Le relecteur voit les champs à corriger en rouge.
  const flushPendingThenRefetch = useCallback(async (): Promise<boolean> => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const fields = pendingSaveRef.current;
    pendingSaveRef.current = {};
    // Échec réseau / 500 : non bloquant comme avant (corrigeable dans la modale).
    const outcome = Object.keys(fields).length === 0 ? 'saved' : await sendReviewFields(fields);
    await queryClient.refetchQueries({ queryKey: ['cgReview', regIdNum] });
    return outcome !== 'fieldErrors';
  }, [regIdNum, queryClient, sendReviewFields]);

  // Rafraîchissement après une soumission depuis la review.
  //
  // `['cgReview']` seul ne suffit pas : l'état du bouton « Soumettre le CTVO »
  // (récépissé obtenu, timeout, numéro de démarche) est calculé depuis
  // `useDealMisivF`, une requête distincte que rien n'invalidait ici. Le bouton
  // restait donc sur son ancien libellé jusqu'au rechargement de la page.
  // Invalidation sur le préfixe de clé : la review n'affiche qu'un dossier.
  const refreshAfterSubmit = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['cgReview', regIdNum] });
    void queryClient.invalidateQueries({ queryKey: ['useDealMisivF'] });
  }, [regIdNum, queryClient]);

  const payload = data?.data;
  const registration = payload?.registration;
  // Deal purgé des blobs (IngestionTrace ~489 Ko, AnalysisSnapshot) que seul
  // getCGData renvoie : les modales Submit* étalent le deal dans le body POSTé
  // sur /api/misiv/*, dont le parser global plafonne à 100 kb → 413 depuis
  // /review alors que list-cg passait. Cf. stripDealBlobs.
  const registrationForSubmit = useMemo(() => stripDealBlobs(registration), [registration]);
  const extractions = useMemo(() => payload?.extractions ?? [], [payload]);
  const unanalyzedDocuments = useMemo<UnanalyzedDocumentRow[]>(() => payload?.unanalyzedDocuments ?? [], [payload]);
  const [analyzingUnanalyzed, setAnalyzingUnanalyzed] = useState(false);
  const [recentlyAnalyzedCount, setRecentlyAnalyzedCount] = useState(0);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);

  // Bandeau « N pièces analysées depuis votre dernière visite » : compare le
  // nombre de pièces non analysées à la dernière visite (localStorage).
  useEffect(() => {
    if (!regIdNum || !payload) return;
    const storageKey = `cgReview:unanalyzedCount:${regIdNum}`;
    const previousRaw = localStorage.getItem(storageKey);
    const previous = previousRaw !== null ? Number(previousRaw) : null;
    const current = unanalyzedDocuments.length;
    if (previous !== null && Number.isFinite(previous) && previous > current) {
      setRecentlyAnalyzedCount(previous - current);
    } else {
      setRecentlyAnalyzedCount(0);
    }
    localStorage.setItem(storageKey, String(current));
  }, [regIdNum, payload, unanalyzedDocuments.length]);

  const analyzeUnanalyzedNow = useCallback(async () => {
    if (!regIdNum || analyzingUnanalyzed) return;
    setAnalyzingUnanalyzed(true);
    setAnalyzeError(null);
    try {
      const response = await makeAuthenticatedApiCall('post', `/api/cg/${regIdNum}/analyze-unanalyzed`, {});
      const analyzed = Number(response?.data?.analyzed ?? 0);
      if (analyzed > 0) setRecentlyAnalyzedCount((n) => n + analyzed);
      await queryClient.invalidateQueries({ queryKey: ['cgReview', regIdNum] });
    } catch {
      setAnalyzeError("L'analyse n'a pas pu être lancée. Réessayez dans un instant.");
    } finally {
      setAnalyzingUnanalyzed(false);
    }
  }, [regIdNum, analyzingUnanalyzed, queryClient]);
  /** Récépissé de DA que Skipcar détient déjà pour ce véhicule (cf. daReceiptDoc). */
  const daReceipt = useMemo(() => payload?.daReceipt ?? null, [payload]);
  const aggregatedFields = useMemo(() => payload?.aggregatedFields ?? [], [payload]);

  // Co-titulaire (acheteur / vendeur) — mêmes valeurs qu'en form-cgia (parité
  // Review = Form). Dérivées des extractions + champs agrégés.
  const reviewCotitulaire = useMemo(
    () => deriveReviewCotitulaire(extractions, aggregatedFields),
    [extractions, aggregatedFields],
  );
  // Décision du relecteur « co-titulaire acheteur : oui / non » (SKIP-454) :
  // prime sur la dérivation OCR, persistée par le PATCH review-fields.
  const [cotitulaireDecision, setCotitulaireDecision] = useState<boolean | null>(null);
  useEffect(() => {
    setCotitulaireDecision(readCotitulaireDecision(payload?.validatedFields));
  }, [payload?.validatedFields]);
  const cotitulaireAcheteurActif = effectiveCotitulaireAcheteur(cotitulaireDecision, reviewCotitulaire.acheteurActif);

  // Commentaires de l'agence (soumission + fil) — normalise le champ `files`
  // (string JSON / 'NULL' / tableau) en tableau d'URLs exploitables.
  const agencyComments = useMemo(() => {
    const parseFiles = (files: string | string[] | null): string[] => {
      if (Array.isArray(files)) return files;
      if (!files || files === 'NULL') return [];
      try {
        const parsed = JSON.parse(files);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };
    return (payload?.externalComments ?? [])
      .filter((c) => c.user_role === 'agency')
      .map((c) => ({ ...c, fileUrls: parseFiles(c.files) }));
  }, [payload]);

  // Tick OK par champ : état local optimiste, hydraté depuis le payload.
  const [validatedFields, setValidatedFields] = useState<
    Record<string, { validated_at?: string; validated_by?: number | null }>
  >({});
  useEffect(() => {
    setValidatedFields(payload?.validatedFields ?? {});
  }, [payload?.validatedFields]);

  const handleToggleFieldValidation = async (fieldKey: string, validated: boolean) => {
    // Optimistic update
    setValidatedFields((prev) => {
      const next = { ...prev };
      if (validated) next[fieldKey] = { validated_at: new Date().toISOString(), validated_by: null };
      else delete next[fieldKey];
      return next;
    });
    try {
      await makeAuthenticatedApiCall('patch', `/api/cg/${regIdNum}/review/validate-field`, {
        fieldKey,
        validated,
      });
    } catch (err: any) {
      // Rollback on failure
      console.error('toggle field validation failed:', err);
      setValidatedFields((prev) => {
        const next = { ...prev };
        if (validated) delete next[fieldKey];
        else next[fieldKey] = { validated_at: new Date().toISOString(), validated_by: null };
        return next;
      });
    }
  };

  const regPlate: string | null = (registration as any)?.RegPlate ?? null;
  const aiConfidence: number | null = (registration as any)?.AIConfidence ?? null;
  const reviewStatus: string = (registration as any)?.CGReviewStatus ?? 'none';

  // Le dossier n'a plus besoin d'être validé manuellement quand :
  //  - il est déjà « god_approved » (validé à la main, ou en attente MISIV type
  //    One-time impayé), ou
  //  - il est en Stage 48 = « Procédure effectuée ». C'est le cas de la
  //    finalisation AUTO déclenchée par une soumission CTVO réussie
  //    (processCGOnCTVOSuccess repasse alors cg_review_status à 'none' → on ne
  //    peut PAS se fier au review status seul, d'où le test sur le stage).
  // Sert au bandeau d'information en tête de review et grise le bouton
  // « Passer en Démarches ANTS » (rétabli le 07/10/2026).
  const alreadyValidated =
    reviewStatus === 'god_approved' || Number((registration as { StageID?: number })?.StageID) === 48;

  // Cross-checks rejoués côté review : mêmes règles métier que FormCGIA,
  // appliquées sur la meilleure extraction de chaque pièce. Les messages
  // précis ("Le véhicule a 5.4 ans, un CT est obligatoire", "VIN différent
  // entre carte grise et cession", …) alimentent les KPIs et tooltips de
  // PiecesGrid — le relecteur voit POURQUOI une pièce bloque, pas juste
  // un compteur.
  const reviewFindings = useMemo(() => {
    const docs: Partial<Record<string, UploadedDocResult>> = {};
    const bestByDocType = new Map<string, ExtractionRow>();
    extractions.forEach((e) => {
      if (!e.docTypeMatch || !isCgDocType(e.docTypeDetected)) return;
      const existing = bestByDocType.get(e.docTypeDetected);
      if (!existing || e.confidence > existing.confidence) {
        bestByDocType.set(e.docTypeDetected, e);
      }
    });
    // Iso form-cgia (useAutoExtractQueue P3.3) : en mode hébergement, la CNI
    // uploadée directement EST celle de l'hébergeur (ses champs ont été lus),
    // tandis que la CNI de l'acquéreur arrive via un fichier multi-pages
    // permis+CNI (warning OCR "contient_aussi_piece_identite", champs vides).
    // La table cg_ocr_extractions ne distingue pas les deux slots — on les
    // recompose ici depuis les signaux disponibles (attestation présente +
    // warning multi-doc) pour que pickAcquereurPi swap correctement et
    // n'alerte pas à tort « fournir la CNI de l'acquéreur ».
    const hasAttestationHeberg = bestByDocType.has('attestation_hebergement');
    const hasMultiDocPi = extractions.some(
      (e) =>
        e.docTypeMatch && Array.isArray(e.warnings) && e.warnings.some((w) => w === 'contient_aussi_piece_identite'),
    );
    const reroutePiToHebergeur = hasAttestationHeberg && hasMultiDocPi;

    bestByDocType.forEach((e, docType) => {
      const targetSlot = reroutePiToHebergeur && docType === 'piece_identite' ? 'piece_identite_hebergeur' : docType;
      docs[targetSlot] = {
        docType: targetSlot as CgDocType,
        firebaseUrls: [e.imageUrl],
        ocrResponse: {
          docTypeDetected: targetSlot as CgDocType,
          docTypeMatch: true,
          docTypeMatchConfidence: e.confidence / 100,
          unreadable: false,
          warnings: [],
          fields: (e.fields ?? {}) as CgOcrResponse['fields'],
        },
      };
    });
    // Fichiers multi-documents : si une extraction confirmée porte le warning
    // "contient_aussi_<docType>" (ex : permis qui contient AUSSI la CNI dans
    // le même PDF), on matérialise un slot virtuel pour <docType> avec des
    // champs vides — sinon les règles de cohérence (ex.
    // coherence_acquereur_cession_vs_pi) ne « voient » pas la pièce et
    // alertent à tort. Iso form-cgia (useAutoExtractQueue, MULTI-DOC).
    extractions.forEach((e) => {
      if (!e.docTypeMatch || !Array.isArray(e.warnings)) return;
      e.warnings.forEach((rawWarning) => {
        if (typeof rawWarning !== 'string') return;
        const m = /^contient_aussi_(.+)$/.exec(rawWarning);
        if (!m || !isCgDocType(m[1])) return;
        const extraDocType = m[1] as CgDocType;
        if (docs[extraDocType]) return;
        docs[extraDocType] = {
          docType: extraDocType,
          firebaseUrls: [e.imageUrl],
          ocrResponse: {
            docTypeDetected: extraDocType,
            docTypeMatch: true,
            docTypeMatchConfidence: e.confidence / 100,
            unreadable: false,
            warnings: [`detecte_dans_fichier_${e.docTypeDetected}`],
            fields: {} as CgOcrResponse['fields'],
          },
        };
      });
    });
    // Équivalence récépissé (EQ-060-GD) : quand la déclaration d'achat a été
    // faite via Skipcar, son récépissé est au dossier DA — on le matérialise
    // ici plutôt que de le réclamer à l'agence. Ne recouvre jamais un récépissé
    // réellement déposé.
    if (!docs.recepisse_da) {
      const repris = buildRecepisseDaDoc(daReceipt);
      if (repris) docs.recepisse_da = repris;
    }

    const report = runCrossChecks(ALL_CROSS_CHECK_RULES, {
      docs,
      now: new Date(),
      userInput: regPlate ? { regPlate } : undefined,
      // Parité form-cgia : la case « co-titulaire vendeur » alimente aussi la
      // règle signature_cession_cotitulaire côté review.
      cotitulaireVendeurManuel: reviewCotitulaire.vendeurActif,
      // Parité form-cgia : les pièces du co-titulaire acquéreur n'ont pas
      // d'extraction OCR — côté review on les retrouve dans les pièces non
      // analysées de provenance `coBuyerFiles`.
      cotitulaireAcheteurFilesCount: unanalyzedDocuments.filter((d) => d.sourceType === 'coBuyerFiles').length,
    });
    // IMPORTANT : on CONSERVE les INFO ici. Contrairement à /form-cgia (où les
    // INFO sont masquées à l'agence et réservées au mode god), la page de review
    // est l'outil interne Skipcar : les notes INFO (MiSIV suspendu, CT valide via
    // X.1, VIN « CG fait foi », SIRET Skipcar pré-imprimé…) sont du diagnostic
    // précieux pour le vérificateur et doivent rester visibles.
    return [...report.errors, ...report.warnings, ...report.infos];
  }, [extractions, regPlate, reviewCotitulaire.vendeurActif, unanalyzedDocuments, daReceipt]);

  // ── Verdict LLM persisté = SOURCE DE VÉRITÉ (iso FormCGIA) ─────────────
  // FormCGIA et review DOIVENT afficher la même analyse. FormCGIA pilote son
  // verdict par le moteur v1 (un-seul-appel Claude) puis l'audit batch Opus,
  // le regex n'étant qu'un filet. La review lit donc ces mêmes verdicts
  // persistés (renvoyés par GET /api/cg/:id/review) au lieu de re-dériver un
  // statut depuis le seul regex — c'était la cause des faux blocages
  // (reconstruction dégradée des pièces côté review).
  const opusResult = payload?.auditBatch?.result ?? null;
  const opusVerdict = payload?.auditBatch?.verdict ?? null;
  const opusFindings = useMemo(() => auditResultToFindings(opusResult), [opusResult]);
  const opusAvailable = opusResult !== null && opusVerdict !== null && opusVerdict !== 'ERROR';

  const v1Result = payload?.analyzeV1?.result ?? null;
  const v1Statut = payload?.analyzeV1?.statut ?? null;
  const v1Findings = useMemo(() => ctvoV1ResultToFindings(v1Result), [v1Result]);
  const v1Available =
    v1Result !== null && v1Statut !== null && v1Statut !== 'analyse_echouee' && v1Statut !== 'en_cours';

  // SOURCE DE VÉRITÉ ABSOLUE : le snapshot figé par FormCGIA à la soumission.
  // S'il existe, la review le rejoue VERBATIM — exactement les findings que
  // l'agence a vus, sans aucune re-dérivation. C'est la garantie d'iso totale.
  const snapshot = payload?.analysisSnapshot ?? null;
  const snapshotFindings = useMemo<CrossCheckFinding[]>(
    () =>
      (snapshot?.findings ?? []).map((f): CrossCheckFinding => ({
        ruleId: f.ruleId,
        severity: f.severity,
        message: f.message ?? '',
        documentTypes: f.documentTypes as CgDocType[] | undefined,
        fields: f.fields,
      })),
    [snapshot],
  );

  // Findings affichés dans PiecesGrid — arbitrage snapshot / verdict LLM / regex
  // délégué à `selectPiecesFindings` (pur, testé) : un snapshot VIDE n'y masque
  // plus un verdict qui, lui, a vu quelque chose (cf. DW-841-DG).
  const piecesFindings = useMemo<CrossCheckFinding[]>(
    () =>
      selectPiecesFindings({
        snapshotFindings,
        llmFindings: v1Available ? v1Findings : opusAvailable ? opusFindings : null,
        regexFindings: reviewFindings,
      }),
    [snapshotFindings, v1Available, v1Findings, opusAvailable, opusFindings, reviewFindings],
  );

  // Le statut des pièces dérive UNIQUEMENT des findings (pas des heuristiques
  // docTypeMismatch/lowConfidence) dès qu'on a un résultat faisant autorité —
  // snapshot OU verdict LLM. C'est ce qui rend la grille review iso avec
  // l'affichage FormCGIA (qui ne dégrade jamais une pièce sur la seule
  // confiance OCR).
  const authoritativeFindings = Boolean(snapshot) || v1Available || opusAvailable;

  // Findings de la colonne Alertes — MÊME source (`piecesFindings`) et MÊME
  // filtre d'affichage (`filterFindingsForDisplay`) que la colonne Alertes de
  // /form-cgia. La review est god-only → `isGod: true` (les INFO restent
  // visibles, comme côté form quand un god teste). C'est ce qui garantit que le
  // compteur "X majeures · Y mineures" est strictement identique : on compte
  // les FINDINGS (pas les pièces). La PiecesGrid ci-dessous reste une vue
  // complémentaire par pièce (un finding multi-pièces y colore plusieurs cards).
  // #4 (retour François DY-565-KJ) — Cohérence titulaire ≠ co-titulaire.
  // Le titulaire (acheteur) et le co-titulaire ne peuvent pas être la MÊME
  // personne. On le détecte sur les entités persistées (Buyer vs CoBuyer) et non
  // sur les docs OCR : c'est là que la confusion se matérialise (cas DY-565-KJ,
  // CARCENAC saisie à la fois en acheteur ET en co-titulaire).
  //
  // Alerte et non blocage : arbitrage `nouveaux_controles_severite` du
  // 09/09/2026 — « alerte seule, le relecteur voit et décide » pour les trois
  // contrôles ajoutés à cette occasion. La review est un écran interne : le
  // signal suffit, et un blocage sur une comparaison de noms persistés
  // immobiliserait un dossier sur une homonymie ou une saisie approximative.
  const coherenceCotitulaireFindings = useMemo<CrossCheckFinding[]>(() => {
    const reg = registration as Record<string, unknown> | undefined;
    if (!reg) return [];
    const norm = (v: unknown) =>
      String(v ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
    const buyerNom = norm(reg.BuyerLastName);
    const buyerPrenom = norm(reg.BuyerFirstName);
    const coNom = norm(reg.CoBuyerLastName);
    const coPrenom = norm(reg.CoBuyerFirstName);
    const hasCobuyer = Boolean(reg.CoBuyerID) && (coNom !== '' || coPrenom !== '');
    if (!hasCobuyer || (buyerNom === '' && buyerPrenom === '')) return [];
    if (buyerNom === coNom && buyerPrenom === coPrenom) {
      return [
        {
          ruleId: 'titulaire_cotitulaire_identiques',
          severity: 'warning',
          message: `Le titulaire et le co-titulaire sont la même personne (${reg.BuyerFirstName ?? ''} ${
            reg.BuyerLastName ?? ''
          }). Le titulaire et le co-titulaire doivent être deux personnes distinctes : corrigez l'identité de l'acheteur ou du co-titulaire (croisez le certificat de cession et le mandat) avant de soumettre le dossier.`,
          documentTypes: ['carte_grise', 'cerfa_cession'] as CgDocType[],
        },
      ];
    }
    return [];
  }, [registration]);

  const alertFindings = useMemo(
    () => filterFindingsForDisplay([...piecesFindings, ...coherenceCotitulaireFindings], { isGod: true }),
    [piecesFindings, coherenceCotitulaireFindings],
  );

  // Findings de la PiecesGrid : mêmes données que `piecesFindings` MOINS les
  // ruleId masqués de l'affichage (`HIDDEN_FROM_AGENCY_RULE_IDS`, ex.
  // `coherence_vin_cg_cession`) — sinon l'alerte retirée de la colonne Alertes
  // resterait visible sur les cards des pièces qu'elle vise. La GodTribune, en
  // revanche, continue de recevoir `piecesFindings` BRUT : c'est une capture
  // exhaustive du contexte, pas un affichage.
  const piecesGridFindings = useMemo(
    () => piecesFindings.filter((f) => !HIDDEN_FROM_AGENCY_RULE_IDS.has(f.ruleId)),
    [piecesFindings],
  );

  // Détection du parcours métier — mêmes ruleId que FormCGIAv2.detectedParcours
  // pour garantir l'iso entre /form-cgia et /cg/:id/review. Présence d'un
  // recepisse_da extrait OU d'un finding parcours_a_* → Parcours A.
  const reviewParcours = useMemo<DetectedParcours>(() => {
    // Priorité 0 : snapshot FormCGIA (parcours figé à la soumission). Sa
    // provenance est persistée depuis septembre 2026 ; pour les dossiers
    // antérieurs on la rétro-déduit du libellé (les 3 libellés « déclaration
    // agence » sont uniques, aucun moteur ne peut les émettre) — sans quoi
    // #13374 et consorts resteraient muets sur l'origine du bandeau.
    if (snapshot) {
      return {
        label: snapshot.parcoursLabel,
        source: snapshot.parcoursSource ?? inferParcoursSourceFromLabel(snapshot.parcoursLabel),
      };
    }
    const parcoursAFindingIds = new Set([
      'parcours_a_requis',
      'recepisse_da_manquant_parcours_a',
      'chronologie_recepisse_da',
    ]);
    // Pas de snapshot (dossiers legacy) : même cascade que FormCGIAv2, sans la
    // déclaration agence qui n'existe que dans le store du formulaire.
    return detectParcours({
      isSuccession: false,
      isProfessionalResale: false,
      v1Label: v1Available ? ctvoV1ParcoursToBannerLabel(v1Result) : null,
      opusParcours: opusAvailable
        ? ((opusResult as { transaction?: { parcours?: string } } | null)?.transaction?.parcours ?? null)
        : null,
      hasParcoursAFinding: reviewFindings.some((f) => parcoursAFindingIds.has(f.ruleId)),
      hasRecepisseDa: extractions.some((e) => e.docTypeDetected === 'recepisse_da' && e.docTypeMatch),
    });
  }, [snapshot, v1Available, v1Result, opusAvailable, opusResult, reviewFindings, extractions]);
  const reviewParcoursLabel = reviewParcours.label;

  // Map fieldKey → AggregatedField pour lookup rapide
  const aggMap = useMemo(() => {
    const m = new Map<string, AggregatedField>();
    aggregatedFields.forEach((f) => m.set(f.fieldKey, f));
    return m;
  }, [aggregatedFields]);

  // Map extractionId → index dans le tableau extractions (pour cliquer sur
  // une pastille source côté droit et faire pivoter le viewer côté gauche)
  const extractionIndexById = useMemo(() => {
    const m = new Map<number, number>();
    extractions.forEach((e, idx) => m.set(e.id, idx));
    return m;
  }, [extractions]);

  // Construction des groupes pour FieldEditList. Les groupes « cœur » (véhicule,
  // vendeur, acheteur, adresse) sont toujours affichés — champs vides inclus, pour
  // que le god complète les infos manquantes avant MISIV. Les groupes OPTIONNELS
  // (conditionnels) sont masqués s'ils n'ont AUCUNE donnée (ex. Co-titulaire
  // quand le véhicule n'en a pas — sinon 5 champs vides inutiles).
  const OPTIONAL_GROUPS = new Set(['Co-titulaire']);
  // Nature de l'entité (vendeur / acheteur) : personne morale vs particulier, pour
  // n'afficher que les champs pertinents (raison sociale + SIRET pour une société,
  // civilité/nom/prénom pour un particulier). RÈGLE UNIQUE `natureVendeur` /
  // `natureAcheteur`, partagée avec l'écran agence (buildVerifyGroups) et les
  // pièces réclamées. Avant l'unification, la review ne lisait QUE le flag
  // `*_est_personne_morale` de la cession : quand il disait « particulier » alors
  // qu'un SIRET avait été lu, le god n'avait sous les yeux NI la raison sociale NI
  // le SIRET — les deux valeurs qui partaient pourtant en base (GP-482-JC).
  const valeurAgregee = (key: string): unknown => aggMap.get(key)?.value;

  /**
   * Bascule particulier / société du relecteur. Elle emprunte le mécanisme
   * d'override existant (`seller_client_type` / `buyer_client_type` sont mappés
   * sur `CG_Entities.EntityType`), donc elle est auto-sauvegardée comme
   * n'importe quelle autre correction — aucune route nouvelle, et la valeur
   * survit au rechargement puisqu'elle est en base.
   */
  const natureChoisie = (fieldKey: string): NatureChoisie | undefined => {
    const brut = overrides[fieldKey] ?? (aggMap.get(fieldKey)?.value as string | undefined);
    if (brut === 'pro') return 'pm';
    if (brut === 'person') return 'particulier';
    return undefined;
  };
  const vendeurChoisi = natureChoisie('seller_client_type');
  const acheteurChoisi = natureChoisie('buyer_client_type');

  // Natures DÉDUITES des pièces, calculées hors du useMemo : la bascule du
  // relecteur les affiche telles quelles tant qu'il n'a rien imposé.
  const vendeurNatureDeduite = natureVendeur({
    flagCession: valeurAgregee('vendeur_est_personne_morale'),
    hasKbisVendeur: extractions.some((e) => e.docTypeDetected === 'kbis_vendeur'),
    vendeurSiret: valeurAgregee('vendeur_siret'),
    vendeurRaisonSociale: valeurAgregee('vendeur_raison_sociale'),
    titulaireSiren: valeurAgregee('titulaire_siren'),
  });
  const acheteurNatureDeduite = natureAcheteur({
    flagCession: valeurAgregee('acheteur_est_personne_morale'),
    // `kbis` non routé = dossier antérieur au dédoublement des slots.
    hasKbis: extractions.some((e) => e.docTypeDetected === 'kbis_acheteur' || e.docTypeDetected === 'kbis'),
    acheteurSiret: valeurAgregee('acheteur_siret'),
    acheteurRaisonSociale: valeurAgregee('acheteur_raison_sociale'),
  });

  const groups = useMemo<FieldGroup[]>(() => {
    const vendeurNature = vendeurNatureDeduite;
    const acheteurNature = acheteurNatureDeduite;
    // Review (god) : on masque « 1ère mise en circulation » et « Marque » — données
    // véhicule dont MiSIV fait foi, non pertinentes à revérifier/éditer ici (elles
    // restent affichées côté agence, cf. buildVerifyGroups).
    const vehiculeFieldsReview = VEHICULE_FIELDS.filter(
      (f) => f.fieldKey !== 'date_premiere_mec' && f.fieldKey !== 'marque',
    );
    // Référence vers le Cerfa 13750 pour les champs du bloc Acheteur (cf.
    // DEMANDE_IMMAT_ACHETEUR_FIELD_KEYS). L'index viewer = index dans
    // `extractions` (ordre contractuel, cf. viewerFiles).
    const demandeImmat = extractions.find((e) => e.docTypeDetected === 'cerfa_demande_immat');
    const demandeImmatRefs: ReferenceDocRef[] = demandeImmat
      ? [
          {
            docType: 'cerfa_demande_immat',
            docIndex: extractions.indexOf(demandeImmat),
            rawFields: demandeImmat.fields,
            coversField: demandeImmatCoversField,
          },
        ]
      : [];
    // Référence vers la CNI du co-titulaire pour les champs du bloc Co-titulaire
    // (cf. PIECE_IDENTITE_COTITULAIRE_FIELD_KEYS). Le slot est celui routé par
    // `withEffectiveDocTypes`, donc directement lisible sur `docTypeDetected`.
    // RECTO uniquement : le verso ne porte ni nom ni prénom (skill CTVO §13.14).
    const piCotitulaire = extractions.find((e) => e.docTypeDetected === 'piece_identite_cotitulaire');
    const piCotitulaireRefs: ReferenceDocRef[] = piCotitulaire
      ? [
          {
            docType: 'piece_identite_cotitulaire',
            docIndex: extractions.indexOf(piCotitulaire),
            rawFields: piCotitulaire.fields,
            coversField: pieceIdentiteCotitulaireCoversField,
          },
        ]
      : [];
    return [
      buildGroup('Véhicule', tokens.color.yellow, vehiculeFieldsReview, aggMap, extractionIndexById),
      buildGroup(
        'Vendeur',
        tokens.color.textMuted,
        filterFieldsByEntityKind(VENDEUR_FIELDS, resoudreNature(vendeurNature, vendeurChoisi)),
        aggMap,
        extractionIndexById,
      ),
      buildGroup(
        'Acheteur',
        tokens.color.blue,
        filterFieldsByEntityKind(ACHETEUR_FIELDS, resoudreNature(acheteurNature, acheteurChoisi)),
        aggMap,
        extractionIndexById,
        demandeImmatRefs,
      ),
      buildGroup('Adresse acheteur', tokens.color.blue, ADRESSE_FIELDS, aggMap, extractionIndexById),
      buildGroup(
        'Co-titulaire',
        tokens.color.purple,
        COTITULAIRE_FIELDS,
        aggMap,
        extractionIndexById,
        piCotitulaireRefs,
      ),
    ].filter(
      (g) =>
        !OPTIONAL_GROUPS.has(g.title) ||
        showCotitulaireGroup(
          cotitulaireDecision,
          g.fields.some((f) => f.value !== '' || f.confidence > 0),
        ),
    );
    // `vendeurChoisi` / `acheteurChoisi` : sans eux dans les dépendances, la
    // bascule du relecteur ne redessinerait les blocs qu'au prochain rechargement.
  }, [
    cotitulaireDecision,
    aggMap,
    extractionIndexById,
    extractions,
    vendeurNatureDeduite,
    acheteurNatureDeduite,
    vendeurChoisi,
    acheteurChoisi,
  ]);

  // Validation par champ affichée dans FieldEditList (bordure + message rouge).
  // Téléphone : doit être un mobile FR (06/07). Les formats internationaux FR sont
  // déjà normalisés à l'affichage ; ce qui reste invalide = numéro étranger / erroné.
  const invalidFields = useMemo<Record<string, string>>(() => {
    const result: Record<string, string> = {};
    const phoneField = groups.find((g) => g.title === 'Acheteur')?.fields.find((f) => f.fieldKey === 'telephone');
    const phone = overrides['telephone'] ?? phoneField?.value ?? '';
    if (phone && !isFrMobile(phone)) {
      result.telephone = 'Numéro non reconnu comme mobile français (attendu 06 ou 07)';
    }
    // Longueurs contrôlées dès la saisie (mêmes bornes que les colonnes), puis
    // refus du serveur au dernier enregistrement — prioritaires, car ce sont
    // eux qui disent ce qui n'a PAS été enregistré.
    return {
      ...result,
      ...findFieldLengthViolations(overrides),
      ...reviewFieldErrorsByRow(serverFieldErrors, groups),
    };
  }, [groups, overrides, serverFieldErrors]);

  const fieldErrorsSummary = useMemo(
    () => summarizeReviewFieldErrors(serverFieldErrors, groups),
    [serverFieldErrors, groups],
  );

  // Fichiers passés à InlineFileViewer.
  //
  // ⚠️ ORDRE CONTRACTUEL : les `extractions` occupent les index 0..n-1, dans le
  // même ordre que le tableau `extractions`. `extractionIndexById` et
  // `unclassifiedDocs` s'appuient sur cette correspondance pour ouvrir le bon
  // document au clic. Les pièces non analysées sont donc APPENDUES en fin de
  // liste, jamais intercalées.
  const viewerFiles = useMemo<ViewerFile[]>(
    () => [
      ...extractions.map((e) => ({
        url: e.imageUrl,
        // Pièce non reconnue par le classifieur : « Type inconnu » répété n
        // fois ne permettait pas de les distinguer — le NOM DE FICHIER est la
        // seule info fiable disponible (retours François 2026-07-29).
        label:
          e.docTypeDetected === 'unknown'
            ? `Non reconnue — ${fileNameFromUrl(e.imageUrl)}`
            : humanizeDocType(e.docTypeDetected),
        docType: e.docTypeDetected,
        confidence: e.confidence,
      })),
      ...unanalyzedDocuments.map((d) => ({
        url: d.url,
        label: d.label,
        unanalyzed: true,
      })),
    ],
    [extractions, unanalyzedDocuments],
  );

  // Documents envoyés mais NON classifiés par le classifieur (Haiku a renvoyé
  // "unknown" — ex. attestation manuscrite illisible). On les surface dans un
  // bloc dédié « Autres documents » pour que le god constate qu'ils existent et
  // les consulte via le carousel (même index que viewerFiles/extractions).
  // Pièces d'identité que le routage n'a rattachées à AUCUNE personne : elles
  // n'alimentent volontairement aucun champ (cf. NON_TITULAIRE_PI_SOURCES côté
  // backend), mais le dossier ne doit pas pour autant les passer sous silence —
  // sans ce bloc, la review affiche « pièce d'identité manquante » alors que le
  // fichier est bien là, seulement non attribué (retours François 17/09 :
  // exemplaire en double de la pièce de l'hébergeur, pièce d'un proche).
  const identityUnroutedDocs = useMemo(
    () => extractions.map((e, idx) => ({ ...e, idx })).filter((e) => e.identityUnrouted),
    [extractions],
  );

  const unclassifiedDocs = useMemo(
    () => extractions.map((e, idx) => ({ ...e, idx })).filter((e) => e.docTypeDetected === 'unknown'),
    [extractions],
  );

  const editedCount = Object.keys(overrides).filter((k) => {
    const original = aggMap.get(k);
    return overrides[k] !== fmtValue(original?.value);
  }).length;

  // Enregistrer SANS valider : persiste les corrections du god (mêmes champs que
  // le flush de l'approbation — diff vs valeur OCR d'origine) dans les tables
  // structurées, mais SANS passer le dossier en god_approved et sans quitter la
  // page. Les ticks « OK » (validatedFields) et les corrections champ par champ
  // sont déjà auto-persistés (validate-field / review-fields debounced) : ce
  // bouton force un flush immédiat de tout et confirme la sauvegarde. Demandé par
  // François : pouvoir enregistrer l'avancement sans déclencher la validation.
  const saveCG = async () => {
    setActionLoading('save');
    setActionError(null);
    try {
      // Annule le debounce en attente : on flushe tout maintenant, de façon
      // exhaustive, pour éviter un double envoi partiel.
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      pendingSaveRef.current = {};
      const editedFields = Object.keys(overrides).reduce<Record<string, string>>((acc, k) => {
        const original = aggMap.get(k);
        if (overrides[k] !== fmtValue(original?.value)) acc[k] = overrides[k];
        return acc;
      }, {});
      // Des champs refusés restent en rouge, avec leur récapitulatif : les
      // autres sont enregistrés, on rafraîchit donc quand même le dossier.
      const outcome = Object.keys(editedFields).length > 0 ? await sendReviewFields(editedFields) : 'saved';
      if (outcome === 'failed') throw new Error("Erreur lors de l'enregistrement — réessayez dans un instant.");
      await queryClient.invalidateQueries({ queryKey: ['cgReview', regIdNum] });
      await queryClient.invalidateQueries({ queryKey: ['useCGList'] });
      setActionLoading(null);
    } catch (err: any) {
      setSaveState('error');
      setActionError(err?.response?.data?.error || err?.message || "Erreur lors de l'enregistrement");
      setActionLoading(null);
    }
  };

  // Passage explicite en « Démarches ANTS en cours » depuis la review (arbitrage
  // pôle CG `demarches_ants_bouton_review` du 07/10/2026). Même chemin que la
  // modale « Changer l'étape CG » (PUT cg-status), qui annule aussi les demandes
  // de pièces encore ouvertes : d'où la confirmation. Les corrections en cours
  // sont enregistrées d'abord, comme avec « Enregistrer ».
  const passerEnDemarchesAnts = async () => {
    const confirme = window.confirm(
      'Passer ce dossier en « Démarches ANTS en cours » ?\n\n' +
        'Les corrections en cours sont enregistrées, et les demandes de pièces encore ouvertes seront annulées.',
    );
    if (!confirme) return;
    setActionLoading('ants');
    setActionError(null);
    try {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      pendingSaveRef.current = {};
      const editedFields = Object.keys(overrides).reduce<Record<string, string>>((acc, k) => {
        const original = aggMap.get(k);
        if (overrides[k] !== fmtValue(original?.value)) acc[k] = overrides[k];
        return acc;
      }, {});
      const outcome = Object.keys(editedFields).length > 0 ? await sendReviewFields(editedFields) : 'saved';
      if (outcome === 'failed') throw new Error("Les corrections n'ont pas pu être enregistrées — réessayez.");

      const response = await makeAuthenticatedApiCall('put', `/api/cg/${regIdNum}/cg-status`, {
        cgStatus: 'awaiting_misiv',
      });
      const data = response.data as { success?: boolean; cgStatus?: string | null; applied?: boolean };
      if (!data?.success) throw new Error('Le changement de statut a échoué.');
      if (data.applied === false) {
        // Le backend a retenu un autre statut (facture impayée…) : le dire.
        window.alert(`Le dossier n'a pas pu passer en « Démarches ANTS en cours » (statut retenu : ${data.cgStatus}).`);
      }
      await queryClient.invalidateQueries({ queryKey: ['cgReview', regIdNum] });
      await queryClient.invalidateQueries({ queryKey: ['useCGList'] });
      setActionLoading(null);
      if (data.applied !== false) allerAuSuivantOuListe();
    } catch (err: any) {
      setActionError(err?.response?.data?.error || err?.message || 'Erreur lors du changement de statut');
      setActionLoading(null);
    }
  };

  const handleRejected = async (_newStatus: string) => {
    setRejectModalOpen(false);
    await queryClient.invalidateQueries({ queryKey: ['cgReview', regIdNum] });
    await queryClient.invalidateQueries({ queryKey: ['useCGList'] });
    allerAuSuivantOuListe();
  };

  if (!validId) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Identifiant de CG invalide.</Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography>Chargement de la demande…</Typography>
      </Box>
    );
  }

  if (isError || !payload) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Impossible de charger la demande : {(error as any)?.message ?? 'erreur inconnue'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: tokens.color.bg, minHeight: '100vh', padding: '20px 24px 28px' }}>
      {/* Layout fluide : plus de plafond `maxWidth` — sur les grands écrans le bloc
          documents s'étend (par la gauche), la colonne des champs restant plafonnée
          à droite (cf. gridTemplateColumns plus bas). */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* ── Hero compact, figé au scroll (immat + agence toujours visibles) ── */}
        <Box
          ref={heroRef}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 30,
            backgroundColor: tokens.color.dark,
            color: tokens.color.white,
            borderRadius: `${tokens.radius['2xl']}px`,
            padding: '12px 22px',
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: tokens.color.yellow,
              }}
            >
              Review Skipcar · Dossier #{regIdNum}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
              <Typography
                sx={{
                  fontSize: '32px',
                  fontWeight: 900,
                  letterSpacing: '0.04em',
                  lineHeight: 1,
                  color: tokens.color.white,
                }}
              >
                {regPlate ?? '—'}
              </Typography>
              {regPlate && (
                <Tooltip title={immatCopiee ? 'Copiée' : "Copier l'immatriculation"}>
                  <IconButton
                    size="small"
                    aria-label="Copier l'immatriculation"
                    onClick={() => copierImmat(regPlate)}
                    sx={{
                      color: immatCopiee ? tokens.color.yellow : '#C7C7C5',
                      border: '1px solid rgba(255,255,255,0.18)',
                      '&:hover': { color: tokens.color.white, borderColor: 'rgba(255,255,255,0.4)' },
                    }}
                  >
                    {immatCopiee ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography sx={{ fontSize: '12px', color: '#C7C7C5', marginTop: '4px' }}>
              {(registration as any)?.BuyerFirstName} {(registration as any)?.BuyerLastName} · Agence{' '}
              {(registration as any)?.AgencyName}
            </Typography>
          </Box>

          <CompactKpi
            label="Pièces"
            value={pieceCounts ? `${pieceCounts.present}/${pieceCounts.required}` : '—'}
          />
          <CompactKpi
            label="Champs édités"
            value={editedCount > 0 ? `+${editedCount}` : '—'}
            highlightColor={editedCount > 0 ? tokens.color.yellow : undefined}
          />
          <CompactKpi
            label="Fiabilité"
            value={aiConfidence != null ? `${aiConfidence}%` : '—'}
            highlightColor={aiConfidence != null ? scoreColor(aiConfidence) : undefined}
          />

          {/* Dossier précédent / suivant dans la file (K / J) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Tooltip title={voisins?.previous ? `Dossier précédent ${voisins.previous.regPlate ?? ''} (K)` : 'Premier de la file'}>
              <span>
                <IconButton
                  size="small"
                  aria-label="Dossier précédent"
                  disabled={!voisins?.previous}
                  onClick={() => voisins?.previous && ouvrirDossier(voisins.previous.registrationId)}
                  sx={{ color: tokens.color.white, border: '1px solid rgba(255,255,255,0.18)', '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)' } }}
                >
                  <KeyboardArrowUpIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Box sx={{ textAlign: 'center', minWidth: 64 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, lineHeight: 1.1 }}>
                {voisins?.position != null ? `${voisins.position} / ${voisins.total}` : '—'}
              </Typography>
              <Typography sx={{ fontSize: '10px', color: '#C7C7C5', lineHeight: 1.1 }}>
                {libelleFileReview(voisins?.queue)}
              </Typography>
            </Box>
            <Tooltip title={voisins?.next ? `Dossier suivant ${voisins.next.regPlate ?? ''} (J)` : 'Dernier de la file'}>
              <span>
                <IconButton
                  size="small"
                  aria-label="Dossier suivant"
                  disabled={!voisins?.next}
                  onClick={() => voisins?.next && ouvrirDossier(voisins.next.registrationId)}
                  sx={{ color: tokens.color.white, border: '1px solid rgba(255,255,255,0.18)', '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)' } }}
                >
                  <KeyboardArrowDownIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ minWidth: 140, textAlign: 'right' }}>
            <StatusPill score={aiConfidence ?? 0} />
            <Typography variant="caption" sx={{ color: '#C7C7C5', display: 'block', marginTop: '4px' }}>
              {humanizeReviewStatus(reviewStatus)}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
              <AuditStatusBadge status={payload?.auditStatus ?? { state: 'never_run' }} />
            </Box>
          </Box>
        </Box>

        {/* ── Bandeau parcours ──────────────────────────────
            Détecté à partir des findings regex (mêmes ruleId que côté
            /form-cgia) pour rester ISO entre les 2 vues : un dossier
            "Parcours A" dans le form doit afficher "Parcours A" dans la
            review. Sinon bug observé : DS-063-CH affichait "Parcours
            classique" en review alors que form-cgia détectait correctement
            le Parcours A via parcours_a_requis. */}
        <ParcoursBanner
          parcours={reviewParcoursLabel}
          details={reviewParcours.source ? PARCOURS_SOURCE_DETAILS[reviewParcours.source] : null}
        />

        {alreadyValidated && (
          <Alert severity="success">
            {Number((registration as { StageID?: number })?.StageID) === 48
              ? 'Cette CG a été validée automatiquement lors de la soumission CTVO (procédure effectuée).'
              : 'Cette CG a déjà été approuvée par Skipcar.'}
          </Alert>
        )}
        {reviewStatus === 'god_rejected' && (
          <Alert severity="error">Cette CG a été rejetée et retournée à l'agence.</Alert>
        )}

        {/* ── Audit IA batch (Claude Opus mode "Claude Desktop") ──
            Désactivé volontairement sur cette page : l'audit tourne désormais
            en sous-marin côté form-cgia (RecapAgencyScreen runPhase1) et
            après fulfill complet (cgReviewController.fulfillDocumentRequest
            avec force=1). Le verdict reste persisté en BDD pour usage futur.
            Pour le réafficher : passer SHOW_AUDIT_BATCH_VERDICT à true.
            Le composant + son endpoint backend restent intacts. */}
        {SHOW_AUDIT_BATCH_VERDICT && <AuditBatchVerdict registrationId={regIdNum} />}

        {/* ── Colonne Alertes — STRICTEMENT iso /form-cgia ──────
            Même composant, même source de findings (snapshot rejoué), même
            filtre d'affichage. Le compteur "X majeures · Y mineures" est donc
            identique à celui que l'agence a vu à la soumission (comptage par
            FINDING, pas par pièce). C'est la réponse à l'iso demandé : la
            review affiche d'abord le verdict exact de FormCGIA. */}
        <AlertsColumn findings={alertFindings} />

        {/* ── Vue récap par pièce (additive A/B avec la vue détaillée ci-dessous) ── */}
        <OpenDocumentRequestsPanel
          registrationId={regIdNum}
          onChanged={() => {
            void queryClient.invalidateQueries({ queryKey: ['cgReview', regIdNum] });
          }}
        />

        <PiecesGrid
          extractions={extractions.map((e) => ({
            id: e.id,
            docTypeDetected: e.docTypeDetected,
            docTypeMatch: e.docTypeMatch,
            confidence: e.confidence,
            fields: e.fields,
            warnings: e.warnings,
            identityUnrouted: e.identityUnrouted,
          }))}
          findings={piecesGridFindings}
          authoritativeFindings={authoritativeFindings}
          onViewDoc={(docType) => {
            const idx = extractions.findIndex((e) => e.docTypeDetected === docType);
            if (idx >= 0) setViewerIndex(idx);
          }}
          onCountsChange={handlePieceCountsChange}
          cotitulaireFilesCount={
            unanalyzedDocuments.filter((d) => d.sourceType === 'coBuyerFiles').length
          }
          openRequestDocTypes={openRequestDocTypes}
          vehicleDateMec={
            typeof registration?.VehicleDateMec === 'string' ? registration.VehicleDateMec : null
          }
        />

        {/* ── Co-titulaire (parité avec form-cgia : mêmes checkboxes/valeurs).
            Toujours affiché : le relecteur peut déclarer un co-titulaire acheteur
            que l'IA n'a pas vu, ou retirer celui qu'elle a inventé (SKIP-454).
            La case « vendeur » reste informative : elle vient de la carte grise. ── */}
        <Box
          sx={{
            backgroundColor: tokens.color.white,
            border: `0.5px solid ${tokens.color.border}`,
            borderRadius: `${tokens.radius.xl}px`,
            boxShadow: tokens.shadow.card,
            overflow: 'hidden',
          }}
        >
          <CotitulaireSection
            acheteurActif={cotitulaireAcheteurActif}
            onToggleAcheteur={(checked) => {
              setCotitulaireDecision(checked);
              scheduleAutoSave('cotitulaire_present', checked ? 'true' : 'false');
            }}
            vendeurActif={reviewCotitulaire.vendeurActif}
            vendeurNom={reviewCotitulaire.vendeurNom}
            vendeurReadOnly
            cotitulaireCniFileName={cotitulaireAcheteurActif ? reviewCotitulaire.cotitulaireLabel : undefined}
          />
        </Box>

        {/* ── Autres documents (envoyés mais non reconnus par l'IA) ── */}
        {identityUnroutedDocs.length > 0 && (
          <Box
            sx={{
              backgroundColor: '#fffaf0',
              border: '1px solid #f0c96b',
              borderRadius: `${tokens.radius.xl}px`,
              boxShadow: tokens.shadow.card,
              padding: '16px 18px',
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: '14px', color: '#8a5a00' }}>
              Pièce(s) d'identité à rattacher ({identityUnroutedDocs.length})
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: tokens.color.textMuted, marginTop: '2px', marginBottom: '10px' }}>
              Déposées au dossier mais rattachées à personne : ni l'acquéreur, ni un co-titulaire déclaré sur la
              demande d'immatriculation, ni un hébergeur attesté. Elles n'alimentent donc AUCUN champ d'identité —
              c'est volontaire. À contrôler : soit c'est un exemplaire en double, soit la personne manque au dossier.
            </Typography>
            <Stack spacing={1}>
              {identityUnroutedDocs.map((e) => (
                <Box
                  key={e.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: `0.5px solid ${tokens.color.border}`,
                    backgroundColor: tokens.color.white,
                  }}
                >
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: tokens.color.dark }}>
                    {humanizeDocType(e.docTypeDetected)} — {fileNameFromUrl(e.imageUrl)}
                  </Typography>
                  <Button size="small" variant="outlined" onClick={() => setViewerIndex(e.idx)}>
                    Voir
                  </Button>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {unclassifiedDocs.length > 0 && (
          <Box
            sx={{
              backgroundColor: tokens.color.white,
              border: `0.5px solid ${tokens.color.border}`,
              borderRadius: `${tokens.radius.xl}px`,
              boxShadow: tokens.shadow.card,
              padding: '16px 18px',
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: '14px', color: tokens.color.dark }}>
              Autres documents ({unclassifiedDocs.length})
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: tokens.color.textMuted, marginTop: '2px', marginBottom: '10px' }}>
              Envoyés par l'agence mais non reconnus automatiquement (ex. pièce manuscrite / scan illisible). À
              consulter dans le carousel et à valider manuellement.
            </Typography>
            <Stack spacing={1}>
              {unclassifiedDocs.map((e) => (
                <Box
                  key={e.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: `0.5px solid ${tokens.color.border}`,
                    backgroundColor: tokens.color.surface,
                  }}
                >
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: tokens.color.dark }}>
                    Document non reconnu #{e.idx + 1}
                    {typeof e.confidence === 'number' ? ` · confiance ${e.confidence}%` : ''}
                  </Typography>
                  <Button size="small" variant="outlined" onClick={() => setViewerIndex(e.idx)}>
                    Voir
                  </Button>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {recentlyAnalyzedCount > 0 && (
          <Alert severity="success" onClose={() => setRecentlyAnalyzedCount(0)} sx={{ borderRadius: '10px' }}>
            {recentlyAnalyzedCount === 1
              ? '1 pièce a été analysée depuis votre dernière visite.'
              : `${recentlyAnalyzedCount} pièces ont été analysées depuis votre dernière visite.`}
          </Alert>
        )}

        {/* ── Pièces au dossier jamais analysées ──
            Elles existent dans list-cg (modale « Soumettre la DC / le CTVO »)
            mais n'ont AUCUNE extraction OCR : ni les cross-checks ni l'audit
            Opus ne les ont vues. Il faut donc les contrôler à l'œil.
            Retours François : EQ-598-TE (20/07, verso PI co-titulaire) puis
            GY-530-NL (22/07, « tu n'as pas pris en compte la pièce d'identité
            alors qu'elle est disponible dans liste cg »). */}
        {unanalyzedDocuments.length > 0 && (
          <Box
            sx={{
              backgroundColor: '#fffaf0',
              border: '1px solid #f0c96b',
              borderRadius: `${tokens.radius.xl}px`,
              boxShadow: tokens.shadow.card,
              padding: '16px 18px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <Typography sx={{ fontWeight: 800, fontSize: '14px', color: '#8a5a00' }}>
                Pièces non analysées ({unanalyzedDocuments.length})
              </Typography>
              <Button
                size="small"
                variant="contained"
                disabled={analyzingUnanalyzed}
                onClick={() => void analyzeUnanalyzedNow()}
                startIcon={analyzingUnanalyzed ? <CircularProgress size={14} color="inherit" /> : undefined}
                sx={{ backgroundColor: '#8a5a00', '&:hover': { backgroundColor: '#6e4700' } }}
              >
                {analyzingUnanalyzed ? 'Analyse…' : 'Analyser maintenant'}
              </Button>
            </Box>
            <Typography sx={{ fontSize: '12.5px', color: tokens.color.textMuted, marginTop: '2px', marginBottom: '10px' }}>
              Présentes au dossier mais jamais passées à l'OCR : aucune vérification automatique n'a été faite dessus
              (ni cohérence inter-pièces, ni audit IA). À contrôler visuellement — ou lancer l'analyse serveur.
            </Typography>
            {analyzeError && (
              <Alert severity="error" sx={{ marginBottom: '10px' }}>
                {analyzeError}
              </Alert>
            )}
            <Stack spacing={1}>
              {unanalyzedDocuments.map((d, i) => (
                <Box
                  key={d.url}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: `0.5px solid ${tokens.color.border}`,
                    backgroundColor: tokens.color.white,
                  }}
                >
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: tokens.color.dark }}>{d.label}</Typography>
                  <Button size="small" variant="outlined" onClick={() => showDocument(extractions.length + i)}>
                    Voir
                  </Button>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* ── Commentaire(s) de l'agence (soumission + fil) ── */}
        {agencyComments.length > 0 && (
          <Box
            sx={{
              backgroundColor: tokens.color.white,
              border: `0.5px solid ${tokens.color.border}`,
              borderRadius: `${tokens.radius.xl}px`,
              boxShadow: tokens.shadow.card,
              padding: '16px 18px',
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: '14px', color: tokens.color.dark, marginBottom: '10px' }}>
              Commentaire de l'agence
            </Typography>
            <Stack spacing={1.5}>
              {agencyComments.map((c) => (
                <Box
                  key={c.comment_id}
                  sx={{
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: `0.5px solid ${tokens.color.border}`,
                    backgroundColor: tokens.color.surface,
                  }}
                >
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: tokens.color.textMuted }}>
                    {c.user_name || 'Agence'}
                  </Typography>
                  <Typography sx={{ fontSize: '13.5px', color: tokens.color.dark, whiteSpace: 'pre-wrap', marginTop: '2px' }}>
                    {c.comment}
                  </Typography>
                  {c.fileUrls.length > 0 && (
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                      {c.fileUrls.map((url, i) => (
                        <Button
                          key={url}
                          size="small"
                          variant="outlined"
                          component="a"
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Pièce jointe {i + 1}
                        </Button>
                      ))}
                    </Stack>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* ── Tribune GOD : désaccord avec l'analyse IA (capture exhaustive) ── */}
        <GodTribune
          registrationId={regIdNum}
          regPlate={regPlate}
          parcours={reviewParcoursLabel}
          parcoursSource={reviewParcours.source}
          aiConfidence={aiConfidence}
          alertFindings={alertFindings}
          piecesFindings={piecesFindings}
          extractions={extractions}
        />

        {/* ── Layout 2 colonnes ───────────────────────────── */}
        <Box
          ref={documentsSectionRef}
          sx={{
            display: 'grid',
            // Viewer = 1fr (fluide, s'étend sur les grands écrans par la gauche) ;
            // champs = ~40 % mais bornés à [440px, 620px] → la colonne de droite reste
            // lisible et de largeur stable, tout l'espace supplémentaire va au document.
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) clamp(440px, 40%, 620px)' },
            gap: '20px',
            alignItems: 'start',
            // Le viewer est `position: sticky; top: 24` : sans cette marge, un
            // scrollIntoView({block:'start'}) collerait le bloc pile au bord haut
            // et laisserait le viewer décalé de 24px sous le sommet.
            scrollMarginTop: `${viewerTop}px`,
          }}
        >
          <InlineFileViewer
            files={viewerFiles}
            currentIndex={viewerIndex}
            onChange={setViewerIndex}
            stickyTop={viewerTop}
            onDelete={supprimerPiece}
          />

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', minHeight: 18, mb: 0.5 }}>
              {saveState === 'saving' && (
                <Typography variant="caption" sx={{ color: tokens.color.textMuted }}>
                  Enregistrement…
                </Typography>
              )}
              {saveState === 'saved' && (
                <Typography variant="caption" sx={{ color: tokens.color.green, fontWeight: 700 }}>
                  ✓ Modifications enregistrées
                </Typography>
              )}
              {saveState === 'error' && (
                <Typography variant="caption" sx={{ color: tokens.color.red, fontWeight: 700 }}>
                  {fieldErrorsSummary.length > 0
                    ? `⚠ ${fieldErrorsSummary.length} champ${fieldErrorsSummary.length > 1 ? 's' : ''} non enregistré${fieldErrorsSummary.length > 1 ? 's' : ''} — entouré${fieldErrorsSummary.length > 1 ? 's' : ''} en rouge ci-dessous`
                    : "⚠ Échec de l'enregistrement — sera retenté à la soumission"}
                </Typography>
              )}
            </Box>
            {/* Même bascule que l'écran agence. Ici elle écrit directement
                `CG_Entities.EntityType` via le flush des champs revus. */}
            <EntityKindSwitches
              vendeurNature={resoudreNature(vendeurNatureDeduite, vendeurChoisi)}
              acheteurNature={resoudreNature(acheteurNatureDeduite, acheteurChoisi)}
              overrides={{ vendeur: vendeurChoisi, acheteur: acheteurChoisi }}
              onChange={(partie, nature) => {
                const fieldKey = partie === 'vendeur' ? 'seller_client_type' : 'buyer_client_type';
                // Relâcher le choix n'efface rien en base : on repose le type
                // que les pièces désignent, seule valeur défendable ici.
                const deduite = partie === 'vendeur' ? vendeurNatureDeduite : acheteurNatureDeduite;
                const valeur =
                  nature === null ? (deduite === 'particulier' ? 'person' : 'pro') : nature === 'pm' ? 'pro' : 'person';
                setOverrides((prev) => ({ ...prev, [fieldKey]: valeur }));
                scheduleAutoSave(fieldKey, valeur);
              }}
            />

            <FieldEditList
              groups={groups}
              overrides={overrides}
              onOverrideChange={(fieldKey, value) => {
                // Téléphone : on normalise à la volée (0033…/+33… → 06/07).
                const v = fieldKey === 'telephone' ? toStorage(value) : value;
                setOverrides((prev) => ({ ...prev, [fieldKey]: v }));
                // Le refus portait sur l'ancienne saisie : il sera réévalué au
                // prochain enregistrement.
                setServerFieldErrors((prev) =>
                  fieldKey in prev ? Object.fromEntries(Object.entries(prev).filter(([k]) => k !== fieldKey)) : prev,
                );
                scheduleAutoSave(fieldKey, v);
              }}
              onJumpToDoc={setViewerIndex}
              validatedFields={validatedFields}
              onToggleFieldValidation={handleToggleFieldValidation}
              invalidFields={invalidFields}
            />
          </Box>
        </Box>

        {fieldErrorsSummary.length > 0 && (
          <Alert severity="error">
            <Typography sx={{ fontWeight: 700, fontSize: '14px', mb: 0.5 }}>
              {fieldErrorsSummary.length > 1
                ? `${fieldErrorsSummary.length} champs n'ont pas été enregistrés — à corriger :`
                : "1 champ n'a pas été enregistré — à corriger :"}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {fieldErrorsSummary.map((item) => (
                <li key={item.rowKey + item.message}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      document
                        .getElementById(reviewFieldAnchorId(item.rowKey))
                        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    }
                    sx={{
                      all: 'unset',
                      cursor: 'pointer',
                      fontWeight: 700,
                      textDecoration: 'underline',
                    }}
                  >
                    {item.label}
                  </Box>{' '}
                  : {item.message}
                </li>
              ))}
            </Box>
          </Alert>
        )}
        {actionError && <Alert severity="error">{actionError}</Alert>}

        {/* ── Action bar ──────────────────────────────────── */}
        <ThemeProvider theme={legacyTheme}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            sx={{ paddingTop: '8px' }}
          >
            <Button
              className="secondaryDealActionButton"
              onClick={() => navigate('/list-cg')}
              disabled={actionLoading !== null}
            >
              ← Retour à la liste
            </Button>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
              <Button
                className="secondaryDealActionButton"
                onClick={() => setRejectModalOpen(true)}
                disabled={actionLoading !== null}
              >
                Demander des pièces complémentaires
              </Button>
              <Button
                className="secondaryDealActionButton"
                onClick={() => void saveCG()}
                disabled={actionLoading !== null}
                startIcon={
                  actionLoading === 'save' ? (
                    <CircularProgress size={18} thickness={5} color="inherit" />
                  ) : undefined
                }
              >
                {actionLoading === 'save'
                  ? 'Enregistrement…'
                  : editedCount > 0
                    ? `Enregistrer ${editedCount} correction${editedCount > 1 ? 's' : ''}`
                    : 'Enregistrer'}
              </Button>
              {/* Retour demandé par le pôle CG le 07/10/2026 : un geste explicite,
                  qui passe par le même chemin que la modale de statut. */}
              <Button
                className="secondaryDealActionButton"
                onClick={() => void passerEnDemarchesAnts()}
                disabled={actionLoading !== null || alreadyValidated}
                startIcon={
                  actionLoading === 'ants' ? <CircularProgress size={18} thickness={5} color="inherit" /> : undefined
                }
              >
                {alreadyValidated ? 'Déjà en Démarches ANTS' : 'Passer en Démarches ANTS'}
              </Button>
              {/* Le bouton « Marquer comme Validé » a été retiré (point Skipcar du
                  08/09/2026). Il posait `god_approved`, donc « Démarches ANTS en
                  cours », depuis l'écran de review : un dossier basculait en
                  démarches sans que personne ne les ait engagées. Ce passage est
                  désormais un geste explicite, via la modale de statut de la
                  liste, qui permet aussi d'en sortir. Les corrections du
                  vérificateur restent enregistrées par le bouton « Enregistrer »
                  ci-dessus, qui ne touche pas au statut. */}
              {/* Boutons de soumission MISIV — mêmes composants que list-cg, montés
                  depuis la review (deal = registration enrichi AllFiles + StageID côté
                  getCGData). Laissés tels quels ; CTVO en dernier = action principale. */}
              {registration != null && (
                <>
                  <SubmitCessionCertificate
                    deal={registrationForSubmit as never}
                    pipelineLetter="f"
                    stageId={Number((registration as { StageID?: number }).StageID ?? 0)}
                    userRole={userRole}
                    onBeforeOpen={flushPendingThenRefetch}
                    onRefresh={refreshAfterSubmit}
                  />
                  <SubmitCTVO
                    deal={registrationForSubmit as never}
                    pipelineLetter="f"
                    stageId={Number((registration as { StageID?: number }).StageID ?? 0)}
                    userRole={userRole}
                    onBeforeOpen={flushPendingThenRefetch}
                    onRefresh={refreshAfterSubmit}
                    emphasis="large"
                  />
                </>
              )}
            </Stack>
          </Stack>
        </ThemeProvider>
      </Box>

      <RejectCGModal
        open={rejectModalOpen}
        registrationId={regIdNum}
        regPlate={regPlate}
        onClose={() => setRejectModalOpen(false)}
        onRejected={(s) => void handleRejected(s)}
      />
    </Box>
  );
};

const CompactKpi = ({
  label,
  value,
  highlightColor,
}: {
  label: string;
  value: React.ReactNode;
  highlightColor?: string;
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 80 }}>
    <Typography
      sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C7C7C5' }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: '24px',
        fontWeight: 800,
        lineHeight: 1,
        color: highlightColor ?? tokens.color.white,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const humanizeReviewStatus = (s: string): string =>
  ({
    none: '—',
    pending_god_review: 'En attente de review',
    god_approved: 'Validé',
    god_rejected: 'Rejeté',
    awaiting_docs: 'En attente de pièces',
    awaiting_payment_and_docs: 'En attente paiement + pièces',
  })[s] ?? s;

// Une instance par dossier : la clé force un remontage complet à chaque
// changement d'identifiant. Sans elle, la navigation dossier suivant /
// précédent (même route) gardait l'état du dossier quitté, dont les
// corrections du relecteur (`overrides`) : « Enregistrer » les écrivait alors
// sur le nouveau dossier. Cas BE-021-RJ du 26/10/2026, qui a reçu l'adresse
// corrigée juste avant sur FH-731-KM.
const CGReviewPage = () => {
  const { registrationId } = useParams<{ registrationId: string }>();
  return (
    <NextThemeProvider>
      <CGReviewInner key={registrationId} />
    </NextThemeProvider>
  );
};

export default CGReviewPage;
